import { useEffect, useRef } from 'react';

import { formatHours, type TaskDto } from '@time-tracking/shared';

import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

interface DeleteTaskDialogProps {
  task: TaskDto | null;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

export function DeleteTaskDialog({ task, onClose, onConfirm, isPending }: DeleteTaskDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  useFocusTrap(dialogRef, Boolean(task));

  useEffect(() => {
    if (!task) return;
    setTimeout(() => confirmBtnRef.current?.focus(), 0);
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [task, onClose]);

  if (!task) return null;

  const loggedMinutes = task.totalMinutes ?? 0;

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="archive-task-dialog-title"
        className="projects-dialog"
        style={{ maxWidth: 480 }}
      >
        <h2 id="archive-task-dialog-title" className="dialog-title">
          Archive task
        </h2>

        <div className="dialog-body">
          <p
            style={{
              color: 'var(--fg2)',
              fontSize: 'var(--fs-16)',
              marginBottom: 'var(--space-3)',
            }}
          >
            Archive <strong>{task.name}</strong>?
          </p>
          {loggedMinutes > 0 ? (
            <div
              className="dialog-delete-warning"
              style={{ background: 'var(--info-bg, #e8f3fb)', borderColor: 'var(--ut-bluebonnet)' }}
            >
              <strong>{formatHours(loggedMinutes)}h already logged.</strong> Archiving hides this
              task from the task list, but all logged hours are preserved in reports.
            </div>
          ) : (
            <p style={{ color: 'var(--fg3)', fontSize: 'var(--fs-14)' }}>
              No hours have been logged on this task.
            </p>
          )}
        </div>

        <div className="dialog-footer">
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className="btn btn--primary"
            onClick={onConfirm}
            disabled={isPending}
            aria-busy={isPending}
          >
            {isPending ? 'Archiving…' : 'Archive task'}
          </button>
        </div>
      </div>
    </div>
  );
}
